/**
 * What `[^label]` and `[^label]: …` match, and which number each label shows.
 *
 * Shared by the Tiptap nodes (`footnote.ts`) and Hearth's `marked` extensions
 * for the share page and email, so a document reads the same in both. Both sit
 * on marked's tokenizer contract: matchers are anchored at the start of `src`
 * and return the exact `raw` they consumed, since marked advances by its length.
 */

export const FOOTNOTE_REFERENCE = "footnoteReference";
export const FOOTNOTE_DEFINITION = "footnoteDefinition";

// No `]` or newline anywhere, and no whitespace at either end — `[^see note]`
// is a label, `[^ 1]` isn't.
const LABEL = "[^\\s\\]](?:[^\\n\\]]*[^\\s\\]])?";
const FOOTNOTE_REFERENCE_RE = new RegExp(`^\\[\\^(${LABEL})\\]`);
const FOOTNOTE_DEFINITION_RE = new RegExp(`^\\[\\^(${LABEL})\\]:[ \\t]*`);
/** Block `start` hint. The full `[^label]:` shape, so prose beginning with a
 * character class doesn't split the paragraph. */
export const FOOTNOTE_DEFINITION_START_RE = new RegExp(
  `^\\[\\^${LABEL}\\]:`,
  "m",
);
/** Inline `start` hint. */
export const FOOTNOTE_REFERENCE_START = "[^";
const CONTINUATION_INDENT_RE = /^(?: {4}|\t)/;
export const CONTINUATION_INDENT = "    ";
export const FOOTNOTE_REFERENCE_INPUT_RE = new RegExp(`\\[\\^(${LABEL})\\]$`);

/** True when `label` can round-trip through `[^label]` unambiguously. */
export function isFootnoteLabel(label: string): boolean {
  return new RegExp(`^${LABEL}$`).test(label);
}

export interface FootnoteReferenceMatch {
  label: string;
  raw: string;
}

/**
 * Escapes and code spans need no handling here: marked only offers a tokenizer
 * a position it has already decided starts an inline token.
 */
export function matchFootnoteReference(
  src: string,
): FootnoteReferenceMatch | undefined {
  const match = FOOTNOTE_REFERENCE_RE.exec(src);
  if (!match) return undefined;
  return { label: match[1]!, raw: match[0] };
}

export interface FootnoteDefinitionMatch {
  label: string;
  raw: string;
  /** The note's body, de-indented — markdown, still to be block-lexed. */
  text: string;
}

/**
 * Takes the first line plus every following line indented four spaces or a tab.
 * Lazy continuation is deliberately unsupported, so a definition can never
 * swallow the block after it.
 */
export function matchFootnoteDefinition(
  src: string,
): FootnoteDefinitionMatch | undefined {
  const match = FOOTNOTE_DEFINITION_RE.exec(src);
  if (!match) return undefined;

  const [first = "", ...rest] = src.slice(match[0].length).split("\n");
  const body = [first];
  // A blank line joins the note only if another indented line follows, so
  // blanks (and the source they span) are held until one does.
  const blanks: string[] = [];
  let held = 0;
  let consumed = match[0].length + first.length;

  for (const line of rest) {
    if (line.trim() === "") {
      blanks.push("");
      held += 1 + line.length;
      continue;
    }
    const indent = CONTINUATION_INDENT_RE.exec(line);
    if (!indent) break;
    body.push(...blanks, line.slice(indent[0].length));
    blanks.length = 0;
    consumed += held + 1 + line.length;
    held = 0;
  }

  return {
    label: match[1]!,
    // Trailing blank lines are left for marked; the newline is taken so the
    // next block starts clean.
    raw: src.slice(0, consumed) + (src[consumed] === "\n" ? "\n" : ""),
    text: body.join("\n"),
  };
}

/** One footnote occurrence, in the order it appears in the document. */
export interface FootnoteMention {
  kind: "reference" | "definition";
  label: string;
}

export interface FootnoteIndex {
  /** Label → the number shown for it, 1-based. */
  numbers: Map<string, number>;
  /** Label → how many references carry it; absent means unreferenced. */
  references: Map<string, number>;
  defined: Set<string>;
}

/**
 * Numbers follow the order references first appear — labels are identifiers,
 * not positions. Unreferenced notes are numbered last, so every marker has one.
 */
export function buildFootnoteIndex(
  mentions: Iterable<FootnoteMention>,
): FootnoteIndex {
  const numbers = new Map<string, number>();
  const references = new Map<string, number>();
  const defined = new Set<string>();
  const definedInOrder: string[] = [];

  for (const { kind, label } of mentions) {
    if (!label) continue;
    if (kind === "reference") {
      references.set(label, (references.get(label) ?? 0) + 1);
      if (!numbers.has(label)) numbers.set(label, numbers.size + 1);
    } else {
      defined.add(label);
      definedInOrder.push(label);
    }
  }

  for (const label of definedInOrder) {
    if (!numbers.has(label)) numbers.set(label, numbers.size + 1);
  }

  return { numbers, references, defined };
}

/** The smallest positive integer not already used as a footnote label. */
export function nextUnusedFootnoteLabel(index: FootnoteIndex): string {
  let candidate = 1;
  while (index.numbers.has(String(candidate))) candidate += 1;
  return String(candidate);
}
